import React, { useMemo, useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress } from '@material-ui/core'
import clsx from 'clsx'
import SydModal from '../../../../components/commonDesign/SydModal'
import { useAIThreadAudit } from '../../../../api/denaliAssistant'
import { useFormattingContext } from '../../../../components/organisms/FormattingProvider/FormattingContext'
import { ICON_NAMES } from '../../../../constants'
import DenaliLogo from '../common/DenaliLogo'
import { escapeHtml, fmtMs, shortHash, printViaIframe } from './threadAuditUtils'

const GRID_COLS = '28px 150px 130px 68px 1fr 36px 64px 68px 72px 66px'

const useStyles = makeStyles(() => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '64px 0'
  },
  error: {
    padding: '24px',
    color: 'var(--color-danger-500)',
    textAlign: 'center'
  },
  noData: {
    padding: '32px',
    textAlign: 'center',
    color: 'var(--color-neutral-500)',
    fontSize: '14px'
  },

  /* Title bar */
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  titleLogo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    flexShrink: 0
  },
  titleName: {
    fontWeight: 500,
    fontSize: '16px',
    color: 'var(--dai-color-text, var(--text-color-main))'
  },
  titleId: {
    fontSize: '13px',
    color: 'var(--color-neutral-400)',
    fontFamily: 'monospace',
    marginLeft: '4px'
  },

  /* Meta row */
  metaRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '14px',
    flexWrap: 'wrap',
    alignItems: 'baseline'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    fontSize: '12px'
  },
  metaLabel: {
    fontWeight: 600,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--color-neutral-400)'
  },
  metaValue: {
    color: 'var(--dai-color-text, var(--text-color-main))'
  },
  metaPlaceholder: {
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic'
  },
  metaId: {
    color: 'var(--color-neutral-400)',
    fontSize: '10px',
    fontFamily: 'monospace'
  },
  metaSep: {
    color: 'var(--color-neutral-200)',
    margin: '0 2px'
  },

  /* Summary strip */
  summaryStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    borderRadius: '8px',
    border: '1px solid var(--color-neutral-200, #e5e7eb)',
    overflow: 'hidden',
    marginBottom: '14px',
    backgroundColor: 'var(--dai-color-surface4, #fff)'
  },
  summaryCell: {
    flex: '1 1 0',
    minWidth: '110px',
    padding: '8px 12px',
    borderRight: '1px solid var(--color-neutral-100, #f3f4f6)',
    '&:last-child': { borderRight: 'none' }
  },
  summaryCellLabel: {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--color-neutral-400)',
    marginBottom: '1px'
  },
  summaryCellValue: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--dai-color-text, var(--text-color-main))',
    fontFamily: 'monospace'
  },
  summaryCellSub: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    marginTop: '1px'
  },

  /* Models inline */
  modelsRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '14px',
    padding: '6px 12px',
    backgroundColor: 'var(--dai-color-surface-subtle, #f9fafb)',
    borderRadius: '6px',
    border: '1px solid var(--color-neutral-100, #f3f4f6)'
  },
  modelChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '11px',
    fontFamily: 'monospace'
  },
  modelName: {
    fontWeight: 600,
    color: 'var(--dai-color-text, var(--text-color-main))'
  },
  modelStat: {
    color: 'var(--color-neutral-500)'
  },

  /* Table */
  tableWrap: {
    borderRadius: '8px',
    border: '1px solid var(--color-neutral-200, #e5e7eb)',
    overflow: 'auto'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: GRID_COLS,
    padding: '0 10px',
    height: '26px',
    alignItems: 'center',
    backgroundColor: 'var(--color-neutral-100, #f3f4f6)',
    borderBottom: '2px solid var(--color-neutral-200, #e5e7eb)',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--color-neutral-500)',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  thR: { textAlign: 'right' },
  thC: { textAlign: 'center' },

  /* User separator row */
  userRow: {
    display: 'grid',
    gridTemplateColumns: '28px 1fr',
    padding: '0 10px',
    height: '24px',
    alignItems: 'center',
    fontSize: '10px',
    borderBottom: '1px solid var(--color-neutral-100, #f3f4f6)',
    backgroundColor: 'var(--dai-color-surface4, #fff)',
    color: 'var(--color-neutral-400)'
  },
  userDot: {
    fontSize: '9px',
    textAlign: 'center',
    color: 'var(--color-neutral-300)'
  },
  userLabel: {
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },

  /* Assistant group header */
  groupRow: {
    display: 'grid',
    gridTemplateColumns: GRID_COLS,
    padding: '0 10px',
    height: '28px',
    alignItems: 'center',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: 'var(--dai-color-surface-subtle, #f9fafb)',
    borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)',
    borderTop: '1px solid var(--color-neutral-100, #f3f4f6)',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'var(--color-neutral-100, #f3f4f6)' }
  },
  groupToggle: {
    fontSize: '9px',
    color: 'var(--color-neutral-400)',
    textAlign: 'center',
    userSelect: 'none'
  },
  groupIntent: {
    fontSize: '9px',
    fontWeight: 600,
    padding: '1px 5px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    backgroundColor: 'var(--color-primary-50, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  flagBadge: {
    fontSize: '8px',
    fontWeight: 700,
    padding: '1px 4px',
    borderRadius: '3px',
    marginLeft: '4px'
  },
  flagError: {
    backgroundColor: 'var(--color-danger-50, #fef2f2)',
    color: 'var(--color-danger-600, #dc2626)'
  },
  flagBlocked: {
    backgroundColor: 'var(--color-warning-50, #fffbeb)',
    color: 'var(--color-warning-600, #d97706)'
  },
  groupActorLabel: {
    color: 'var(--color-primary-700, #1d4ed8)',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    gridColumn: 'span 3'
  },
  numR: {
    textAlign: 'right',
    fontFamily: 'monospace',
    fontSize: '11px',
    color: 'var(--color-neutral-600)'
  },
  costR: {
    textAlign: 'right',
    fontFamily: 'monospace',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--dai-color-text, var(--text-color-main))'
  },

  /* Invocation row */
  invRow: {
    display: 'grid',
    gridTemplateColumns: GRID_COLS,
    padding: '0 10px',
    minHeight: '28px',
    alignItems: 'center',
    fontSize: '11px',
    borderBottom: '1px solid var(--color-neutral-50, #f9fafb)',
    backgroundColor: 'var(--dai-color-surface4, #fff)',
    '&:hover': { backgroundColor: 'var(--color-neutral-50, #fafafa)' }
  },
  invNum: {
    fontSize: '9px',
    color: 'var(--color-neutral-400)',
    textAlign: 'center',
    fontFamily: 'monospace',
    fontWeight: 600
  },
  cellClip: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
    fontSize: '11px',
    color: 'var(--dai-color-text, var(--text-color-main))'
  },
  ver: {
    fontFamily: 'monospace',
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)'
  },
  promptCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
    padding: '2px 0',
    minWidth: 0
  },
  promptTag: {
    fontSize: '9px',
    fontWeight: 500,
    padding: '0px 4px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    border: '1px solid var(--color-neutral-200, #e5e7eb)',
    color: 'var(--color-neutral-500)',
    whiteSpace: 'nowrap',
    lineHeight: '16px'
  },
  promptTagInstruction: {
    backgroundColor: 'var(--color-success-50, #f0fdf4)',
    borderColor: 'var(--color-success-200, #bbf7d0)',
    color: 'var(--color-success-700, #15803d)'
  },
  promptTagSkill: {
    backgroundColor: 'var(--color-primary-50, #eff6ff)',
    borderColor: 'var(--color-primary-200, #bfdbfe)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  promptTagTone: {
    backgroundColor: 'var(--color-warning-50, #fffbeb)',
    borderColor: 'var(--color-warning-200)',
    color: 'var(--color-warning-700)'
  },
  promptTagTools: {
    backgroundColor: 'var(--color-danger-50, #fff4ee)',
    borderColor: 'var(--color-danger-200, #fed7c3)',
    color: 'var(--color-danger-700, #c2410c)'
  },
  hashFrag: {
    opacity: 0.5
  },
  rndCell: {
    textAlign: 'center',
    fontFamily: 'monospace',
    fontSize: '10px',
    color: 'var(--color-neutral-500)'
  },

  /* Tool call sub-row */
  toolRow: {
    padding: '0 10px 0 38px',
    fontSize: '10px',
    fontFamily: 'monospace',
    color: 'var(--color-neutral-400)',
    lineHeight: '18px',
    borderBottom: '1px solid var(--color-neutral-50, #f9fafb)',
    backgroundColor: 'var(--dai-color-surface4, #fff)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  toolOk: { color: 'var(--color-success-700, #15803d)' },
  toolErr: { color: 'var(--color-danger-600, #dc2626)' }
}))

function PromptTags ({ prompt, classes }) {
  const visible = prompt?.filter(b => b.type !== 'other')
  if (!visible?.length) return <span style={{ color: 'var(--color-neutral-300)', fontSize: '10px' }}>—</span>

  return visible.map((block, j) => {
    let label = block.type
    if (block.metadata?.skillId) label += `:${block.metadata.skillId}`
    if (block.metadata?.version != null) label += `@v${block.metadata.version}`
    if (block.type === 'tools' && block.metadata?.count) label += `(${block.metadata.count})`
    const hash = shortHash(block.hash)
    return (
      <span
        key={j}
        className={clsx(
          classes.promptTag,
          block.type === 'instruction' && classes.promptTagInstruction,
          block.type === 'skill' && classes.promptTagSkill,
          block.type === 'tone' && classes.promptTagTone,
          block.type === 'tools' && classes.promptTagTools
        )}
        title={block.hash || ''}
      >
        {label}{hash ? <span className={classes.hashFrag}>:{hash}</span> : null}
      </span>
    )
  })
}

PromptTags.propTypes = {
  prompt: PropTypes.array,
  classes: PropTypes.object.isRequired
}

function AssistantMessageGroup ({ msg, classes, formatter, printMode }) {
  const [expanded, setExpanded] = useState(true)
  const isExpanded = printMode || expanded
  const flags = msg.generation || {}
  const invocations = msg.invocations || []
  const hasDetail = invocations.length > 0

  const totIn = invocations.reduce((s, v) =>
    s + (v.inputTokens || 0) + (v.cacheReadInputTokens || 0) + (v.cacheWriteInputTokens || 0), 0)
  const totOut = invocations.reduce((s, v) => s + (v.outputTokens || 0), 0)
  const totLat = invocations.reduce((s, v) => s + (v.latencyMs || 0), 0)

  return (
    <>
      <div className={classes.groupRow} onClick={() => hasDetail && setExpanded(e => !e)}>
        <span className={classes.groupToggle}>{hasDetail ? (isExpanded ? '▾' : '▸') : ''}</span>
        <span className={classes.groupActorLabel}>
          assistant · {formatter(msg.createdDate, 'localDateTime')}
        </span>
        <div>
          {msg.intent && <span className={classes.groupIntent}>{msg.intent}</span>}
          {flags.isError && <span className={clsx(classes.flagBadge, classes.flagError)}>ERR</span>}
          {flags.isBlocked && <span className={clsx(classes.flagBadge, classes.flagBlocked)}>BLK</span>}
        </div>
        <span className={classes.rndCell}>{invocations.length}</span>
        <span className={classes.numR}>{fmtMs(totLat)}</span>
        <span className={classes.costR}>{formatter(msg.cost, '$0,0.0000')}</span>
        <span className={classes.numR}>{formatter(totIn, 'number')}</span>
        <span className={classes.numR}>{formatter(totOut, 'number')}</span>
      </div>

      {isExpanded && invocations.map((inv, i) => {
        const tools = inv.toolCalls || []
        return (
          <React.Fragment key={i}>
            <div className={classes.invRow}>
              <span className={classes.invNum}>{i + 1}</span>
              <span className={classes.cellClip} title={inv.taskCode}>{inv.taskCode}</span>
              <span className={classes.cellClip} title={inv.modelId}>{inv.modelId}</span>
              <span className={classes.ver} title={inv.refVersion || ''}>{shortHash(inv.refVersion) || '—'}</span>
              <div className={classes.promptCell}>
                <PromptTags prompt={inv.systemPrompt} classes={classes} />
              </div>
              <span className={classes.rndCell}>{inv.round ?? '—'}</span>
              <span className={classes.numR}>{fmtMs(inv.latencyMs)}</span>
              <span className={classes.costR}>{formatter(inv.cost, '$0,0.0000')}</span>
              <span className={classes.numR}>{formatter((inv.inputTokens || 0) + (inv.cacheReadInputTokens || 0) + (inv.cacheWriteInputTokens || 0), 'number')}</span>
              <span className={classes.numR}>{formatter(inv.outputTokens, 'number')}</span>
            </div>
            {tools.length > 0 && (
              <div className={classes.toolRow}>
                {'↳ '}
                {tools.map((tc, j) => (
                  <React.Fragment key={j}>
                    {j > 0 && ' · '}
                    <span className={tc.status === 'error' ? classes.toolErr : classes.toolOk}>
                      {tc.name}
                    </span>
                    {tc.status === 'success' ? ' ✓' : ' ✗'}
                    {tc.durationMs != null && ` ${fmtMs(tc.durationMs)}`}
                  </React.Fragment>
                ))}
              </div>
            )}
          </React.Fragment>
        )
      })}
    </>
  )
}

AssistantMessageGroup.propTypes = {
  msg: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  formatter: PropTypes.func.isRequired,
  printMode: PropTypes.bool
}

function AuditContent ({ thread, printMode, contentRef }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const s = thread.summary || {}

  const totalAllInput = (s.totalInputTokens || 0) + (s.totalCacheReadInputTokens || 0) + (s.totalCacheWriteInputTokens || 0)
  const cacheHitPct = totalAllInput > 0
    ? Math.round(((s.totalCacheReadInputTokens || 0) / totalAllInput) * 100)
    : 0

  return (
    <div ref={contentRef}>
      {/* Meta */}
      <div className={classes.metaRow}>
        <div className={classes.metaItem}>
          <span className={classes.metaLabel}>Owner</span>
          {thread.ownerName
            ? <span className={classes.metaValue}>{thread.ownerName} <span className={classes.metaId}>({thread.ownerUserId})</span></span>
            : <span className={classes.metaPlaceholder}>User #{thread.ownerUserId}</span>}
        </div>
        <span className={classes.metaSep}>·</span>
        <div className={classes.metaItem}>
          <span className={classes.metaLabel}>Firm</span>
          {thread.firmName
            ? <span className={classes.metaValue}>{thread.firmName} <span className={classes.metaId}>({thread.firmId})</span></span>
            : <span className={classes.metaPlaceholder}>Firm #{thread.firmId}</span>}
        </div>
        <span className={classes.metaSep}>·</span>
        <div className={classes.metaItem}>
          <span className={classes.metaLabel}>Created</span>
          <span className={classes.metaValue}>{formatter(thread.createdDate, 'localDateTime')}</span>
        </div>
        <span className={classes.metaSep}>·</span>
        <div className={classes.metaItem}>
          <span className={classes.metaLabel}>Last Active</span>
          <span className={classes.metaValue} title={formatter(thread.lastActivity, 'since')}>{formatter(thread.lastActivity, 'localDateTime')}</span>
        </div>
        {thread.focus && (
          <>
            <span className={classes.metaSep}>·</span>
            <div className={classes.metaItem}>
              <span className={classes.metaLabel}>Focus</span>
              <span className={classes.metaValue}>{thread.focus.type}: {thread.focus.subjectLabel}</span>
            </div>
          </>
        )}
      </div>

      {/* Summary strip */}
      <div className={classes.summaryStrip}>
        <div className={classes.summaryCell}>
          <div className={classes.summaryCellLabel}>Cost</div>
          <div className={classes.summaryCellValue}>{formatter(s.totalCost, '$0,0.0000')}</div>
        </div>
        <div className={classes.summaryCell}>
          <div className={classes.summaryCellLabel}>Invocations</div>
          <div className={classes.summaryCellValue}>{s.totalInvocations}</div>
          <div className={classes.summaryCellSub}>{s.totalToolCalls} tool calls</div>
        </div>
        <div className={classes.summaryCell}>
          <div className={classes.summaryCellLabel}>Input Tokens</div>
          <div className={classes.summaryCellValue}>{formatter(totalAllInput, 'number')}</div>
          {cacheHitPct > 0 && <div className={classes.summaryCellSub}>{cacheHitPct}% cache hit</div>}
        </div>
        <div className={classes.summaryCell}>
          <div className={classes.summaryCellLabel}>Output Tokens</div>
          <div className={classes.summaryCellValue}>{formatter(s.totalOutputTokens, 'number')}</div>
        </div>
        <div className={classes.summaryCell}>
          <div className={classes.summaryCellLabel}>Latency</div>
          <div className={classes.summaryCellValue}>{fmtMs(s.totalLatencyMs)}</div>
        </div>
        <div className={classes.summaryCell}>
          <div className={classes.summaryCellLabel}>Errors / Blocked</div>
          <div className={classes.summaryCellValue}>{s.errors || 0} / {s.blocked || 0}</div>
        </div>
      </div>

      {/* Models */}
      {s.models?.length > 0 && (
        <div className={classes.modelsRow}>
          {s.models.map((m, i) => (
            <div key={i} className={classes.modelChip}>
              <span className={classes.modelName}>{m.modelId}</span>
              <span className={classes.modelStat}>{m.invocations}× · {formatter(m.inputTokens + m.outputTokens, 'number')} tok · {formatter(m.cost, '$0,0.0000')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Invocation table */}
      <div className={classes.tableWrap}>
        <div className={classes.tableHeader}>
          <span className={classes.thC}>#</span>
          <span>Task</span>
          <span>Model</span>
          <span>Ver</span>
          <span>Prompt Blocks</span>
          <span className={classes.thC}>Rnd</span>
          <span className={classes.thR}>Latency</span>
          <span className={classes.thR}>Cost</span>
          <span className={classes.thR}>In</span>
          <span className={classes.thR}>Out</span>
        </div>

        {(thread.messages || []).map((msg, i) => {
          if (msg.actor === 'assistant') {
            return <AssistantMessageGroup key={msg.threadMessageId || i} msg={msg} classes={classes} formatter={formatter} printMode={printMode} />
          }
          return (
            <div key={msg.threadMessageId || i} className={classes.userRow}>
              <span className={classes.userDot}>○</span>
              <span className={classes.userLabel}>
                user <span style={{ fontWeight: 400 }}>{formatter(msg.createdDate, 'localDateTime')}</span>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

AuditContent.propTypes = {
  thread: PropTypes.object.isRequired,
  printMode: PropTypes.bool,
  contentRef: PropTypes.object
}

function ThreadAuditModal ({ threadId, open, onClose }) {
  const classes = useStyles()
  const { data, isLoading, isError, error } = useAIThreadAudit(threadId, { enabled: open })
  const thread = useMemo(() => data?.thread || null, [data])
  const contentRef = useRef(null)
  const [printMode, setPrintMode] = useState(false)

  const handleDownloadPdf = useCallback(() => {
    if (!contentRef.current) return
    setPrintMode(true)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const content = contentRef.current
        if (!content) { setPrintMode(false); return }

        const header = [
          '<div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">',
          `<span style="font-weight:500;font-size:16px;font-family:serif">${escapeHtml(thread?.name || 'Untitled Thread')}</span>`,
          `<span style="font-size:13px;color:#999;font-family:monospace">${escapeHtml(thread?.threadId || threadId)}</span>`,
          '</div>'
        ].join('')

        printViaIframe(
          header + content.innerHTML,
          `Denali AI Audit - ${thread?.threadId || threadId} - ${new Date().toISOString().slice(0, 10)}`,
          () => setPrintMode(false)
        )
      })
    })
  }, [thread, threadId])

  const headerTitle = (
    <div className={classes.titleBar}>
      <div className={classes.titleLogo}><DenaliLogo size='24px' /></div>
      <span className={clsx(classes.titleName, 'font-serif')}>{thread?.name || 'Untitled Thread'}</span>
      <span className={classes.titleId}>{thread?.threadId || threadId}</span>
    </div>
  )

  const otherActions = thread
    ? [{ icon: ICON_NAMES.print, onClick: handleDownloadPdf }]
    : []

  return (
    <SydModal
      titleContent={headerTitle}
      open={open}
      onClose={onClose}
      size='full'
      position='top'
      disablePortal={false}
      bodyPadding='0 28px 28px'
      otherActions={otherActions}
    >
      {isLoading && (
        <div className={classes.loading}>
          <CircularProgress size={32} />
        </div>
      )}
      {isError && (
        <div className={classes.error}>
          Failed to load audit data: {error?.message || 'Unknown error'}
        </div>
      )}
      {!isLoading && !isError && !thread && (
        <div className={classes.noData}>No audit data available for this thread.</div>
      )}
      {thread && <AuditContent thread={thread} printMode={printMode} contentRef={contentRef} />}
    </SydModal>
  )
}

ThreadAuditModal.propTypes = {
  threadId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ThreadAuditModal
